import React, { Suspense, lazy } from "react";
import PropTypes from "prop-types";
import { Grid, Loader, Segment } from "semantic-ui-react";

import { BrowserRouter, Route } from "react-router-dom";

import titleFromUrl, { brandFromUrl } from "utils/titleFromUrl";

import DeleteDocumentConfirm from "features/components/related-documents/delete/component/Confirm.jsx";
import { ToolbarStripFrame, ToolbarStripProvider } from "components/ToolbarStrip";

import "./layout.scss";

import { CurrentUser } from "utils/user.js";
import AssistantLauncher from "features/assistant";

const Profile = lazy(() => import("./Profile"));
const Header = lazy(() => import("./Header"));

const Totals = lazy(() => import("./Totals"));
const POTotals = lazy(() =>
  import("features/components/totals/purchase_orders")
);
const PITotals = lazy(() =>
  import("features/components/totals/payment_instructions")
);
const InvTotals = lazy(() => import("features/components/totals/invoices"));

const RLTotals = lazy(() =>
  import("features/components/totals/requisition_lists")
);
const PSTotals = lazy(() =>
  import("features/components/totals/partner_schemes")
);
const ImportTotals = lazy(() =>
  import("features/components/totals/invoice_imports")
);

const Navigation = lazy(() => import("./Navigation"));
const CompanySelect = lazy(() => import("./CompanySelect"));

const ToolBarActionsPI = lazy(() =>
  import("features/payment-instructions/component/ToolBarActions")
);
const ToolBarActionsMA = lazy(() =>
  import("features/master-accounts/component/ToolBarActions")
);
const ToolBarActionsRecurring = lazy(() =>
  import("features/recurring-templates/component/ToolBarActions")
);
const ToolBarActionsRL = lazy(() =>
  import("features/requisition-lists/component/ToolBarActions")
);
const ToolBarActionsDN = lazy(() =>
  import("features/delivery-notes/component/ToolBarActions")
);
const ToolBarActionsInv = lazy(() =>
  import("features/invoices/component/ToolBarActions")
);
const ToolBarActionsPayroll = lazy(() =>
  import("features/payrolls/component/ToolBarActions")
);
const ToolBarActionsCompanies = lazy(() =>
  import("features/companies/component/ToolBarActions")
);

const CompanyPayrollsSelect = lazy(() =>
  import("features/payrolls/component/CompanySelect")
);
const CompanyPIMultiSelect = lazy(() =>
  import("features/payment-instructions/component/CompanyMultiSelect")
);
const CompanyDNMultiSelect = lazy(() =>
  import("features/delivery-notes/component/CompanyMultiSelect")
);
const CompanyPOMultiSelect = lazy(() =>
  import("features/purchase-orders/components/CompanyMultiSelect.jsx")
);
const CompanyInvMultiSelect = lazy(() =>
  import("features/invoices/component/CompanyMultiSelect.jsx")
);
const CompanyImportMultiSelect = lazy(() =>
  import("features/invoice-imports/component/CompanyMultiSelect.jsx")
);
const VesselRlMultiSelect = lazy(() =>
  import("features/requisition-lists/component/FiltersRow/VesselFilter")
);
const VesselMasterAccountSelect = lazy(() =>
  import("features/master-accounts/component/VesselFilter")
);
const VesselDashboardSelect = lazy(() =>
  import("features/dashboard/component/VesselFilter")
);
// DAX Data (FIN-1357): the ship's Master Account or its Position list.
const DaxDataViewSwitch = lazy(() =>
  import("features/master-accounts/component/ViewSwitch")
);

const FiltersRowRL = lazy(() =>
  import("features/requisition-lists/component/FiltersRow")
);

const currentUser = new CurrentUser();

const TotalComponent = () => (
  <BrowserRouter>
    <div className="app-totals">
      <Route component={Totals} path="/invoices" />
      <Route component={InvTotals} path="/invoices_new" />
      <Route component={RLTotals} path="/requisition_lists" />
      <Route component={POTotals} path="/purchase_orders" />
      <Route component={PITotals} path="/payment_instructions" />
      <Route component={PSTotals} path="/partner_schemes" />
      <Route component={ImportTotals} path="/invoice_imports" exact />
      <Route component={DaxDataViewSwitch} path="/dax_info" />
    </div>
  </BrowserRouter>
);

const CompanySelectComponent = () => (
  <BrowserRouter>
    <Route component={CompanySelect} path="/invoices" />
    <Route component={CompanyInvMultiSelect} path="/invoices_new" />
    <Route component={CompanyImportMultiSelect} path="/invoice_imports" />
    <Route component={CompanyPayrollsSelect} path="/payrolls" />
    <Route component={CompanyPIMultiSelect} path="/payment_instructions" />
    <Route component={CompanyDNMultiSelect} path="/delivery_notes" />
    <Route component={CompanyPOMultiSelect} path="/purchase_orders" />
    <Route component={VesselRlMultiSelect} path="/requisition_lists" />
    <Route component={VesselMasterAccountSelect} path="/dax_info" />
    <Route component={VesselDashboardSelect} path="/dashboard" />
  </BrowserRouter>
);

const Layout = (Page) => {
  const PageWithHeader = (props) => (
    <div className="ui container">
      <DeleteDocumentConfirm />
      <Suspense fallback={<Loader active inline="centered" />}>
        <ToolbarStripProvider>
          <Grid>
            <Grid.Row className="app-header-row">
              <Grid.Column width={4} textAlign="left">
                <Header title={titleFromUrl(props.match.path)} brand={brandFromUrl(props.match.path)} />
              </Grid.Column>
              <Grid.Column width={8} textAlign="center">
                <TotalComponent />
              </Grid.Column>
              <Grid.Column width={4} textAlign="right">
                <Grid>
                  <Grid.Row>
                    <Grid.Column>
                      <AssistantLauncher />
                      <Profile push={props.history.push} />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row floated="right">
                    <Grid.Column
                      id="right-column-select-company"
                      verticalAlign="bottom"
                      textAlign="right"
                    >
                      <CompanySelectComponent />
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row className="app-filters-row">
                <BrowserRouter>
                  <Route component={FiltersRowRL} path="/requisition_lists" />
                </BrowserRouter>
            </Grid.Row>
            {/* FIN-1297: the strip, one rule on every page (components/ToolbarStrip). The toolbars
                routed here and the pages themselves put their controls into it. */}
            <Grid.Row className="app-actions-row">
              <ToolbarStripFrame
                menu={
                  <Navigation
                    path={props.match.path}
                    push={props.history.push}
                    currentUser={currentUser}
                  />
                }
              />
              <BrowserRouter>
                <Route component={ToolBarActionsCompanies} path="/companies" />
                <Route component={ToolBarActionsPI} path="/payment_instructions" />
                <Route component={ToolBarActionsDN} path="/delivery_notes" />
                <Route component={ToolBarActionsInv} path="/invoices_new" />
                <Route component={ToolBarActionsPayroll} path="/payrolls" />
                <Route component={ToolBarActionsRL} path="/requisition_lists" />
                <Route component={ToolBarActionsMA} path="/dax_info" />
                <Route component={ToolBarActionsRecurring} path="/recurring_templates" />
              </BrowserRouter>
            </Grid.Row>
          </Grid>

          <section className="app-content">
            <Page {...props} />
          </section>
        </ToolbarStripProvider>
      </Suspense>
    </div>
  );

  PageWithHeader.propTypes = {
    match: PropTypes.shape({
      path: PropTypes.string.isRequired,
    }).isRequired,
    history: PropTypes.shape({
      replace: PropTypes.func,
      push: PropTypes.func,
    }).isRequired,
  };

  return PageWithHeader;
};

export default Layout;
